import React, { Fragment } from "react";
import "bootstrap/dist/js/bootstrap.js";
import "../../styles/css/boston-bounce-2.min.css";
//import '../../styles/css/fontawesome-pro-5.14.0-web/css/all.min.css';
import { NavBar, NavBarTypes, ResponsiveTypes, NavItemProps } from "../Nav";
import GoogleAnalytics from "../GoogleAnalytics";
import Div100vh from "react-div-100vh";
import { huiLog } from "../../common/huiConsoleLogs";

export interface AtlasPageProps {
	name: string;
	subtitle?: string;
	navigation?: NavBar | any;
	utilityNavigation?: NavItemProps[];
	content: any;
	contentAriaLabel?: string;
	environment?: ProductionEnvironment;
	theme?: AtlasTheme;
	profile?: any;
	customLogo?: any;
	onLogoClick?: (event: React.MouseEvent) => void;
	gaId: string;
	inverseHeading?: boolean;
	utilMaxWidth?: number;
	utilMinWidth?: number;
}

export type AtlasTheme = "default" | "medical" | "medical-dark" | "max-contrast" | "ruby" | "travel";

export type ProductionEnvironment = "production" | "staging" | "dev" | "admin" | "superuser" | "helpdesk" | "online" | "offline";

export interface AtlasFeedbackProps {
	name: string;
	icon: string;
	onSubmit: any;
	formName?: string;
	formEmail?: string;
	formComment?: string;
}

export default class AtlasPage extends React.Component<AtlasPageProps> {
	mainContentRef: React.RefObject<any>;

	constructor(props: AtlasPageProps) {
		super(props);
		this.mainContentRef = React.createRef();
		this.skipToMainContent = this.skipToMainContent.bind(this);
	}

	componentDidMount() {
		if (this.props.navigation && this.props.navigation.props && this.props.navigation.props.tabs && this.props.navigation.props.tabs.length === 1)
			document.getElementsByClassName("atlas-theme")[0].classList.add("single-item");
	}

	public static defaultProps = {
		name: "New Application",
		theme: "default",
	};

	getClasses(): string {
		var classes: string[] = [];

		if (this.props.theme === "default") {
			classes.push("mit-bounce");
		}
		if (this.props.theme === "medical") {
			classes.push("medical");
		}
		if (this.props.theme === "medical-dark") {
			classes.push("medical-dark");
		}

		if (this.props.theme === "max-contrast") {
			classes.push("max-contrast");
		}

		if (this.props.theme === "travel") {
			classes.push("travel");
		}

		if (this.props.inverseHeading) {
			// this is mostly for content type sites with a heroshot area where we do not know what it will be
			classes.push("text-white");
		}

		return classes.join(" ");
	}

	skipToMainContent(): void {
		if (this.mainContentRef) {
			this.mainContentRef.current.focus();
		}
	}

	render(): React.ReactNode {
		if (!this.props.contentAriaLabel) {
			huiLog.updateWarning(
				`\nA UNIQUE [contentAriaLabel] attributes will soon become required for all [AtlasPage] components. 
				\nPlease update all [AtlasPage] components with this atrribute ASAP.`
			);
		}

		return (
			<Fragment>
				<GoogleAnalytics gaId={this.props.gaId} />
				<div className={` ${this.getClasses()}`}>
					<button className="sr-only sr-only-focusable skip-content" onClick={this.skipToMainContent} tabIndex={0}>
						Skip to main content
					</button>
				</div>

				<Div100vh>
					<div className={`atlas-theme ${this.getClasses()}`}>
						<span className="mobile-handle">
							<i className="fas fa-chevron-down" />
						</span>
						<div className={`sidebar`} role="navigation" aria-label="Primary">
							<div className="logo">
								<div className="mb-3">
									{this.props.customLogo ? (
										this.props.customLogo
									) : (
										<a onClick={(e: React.MouseEvent) => this.props.onLogoClick && this.props.onLogoClick(e)}>
											<svg width="49px" height="54px" viewBox="0 0 49 54" version="1.1">
												<title>Atlas Logo</title>
												<g id="Symbols" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
													<g id="Primary-Menu" transform="translate(-22.000000, -21.000000)">
														<g id="Logo" transform="translate(22.000000, 21.000000)">
															<path
																d="M28.3506045,0 L49,54 L32.1588946,54 L29.3238342,46.6401869 L15.275475,46.6401869 L12.3557858,54 L8.50518135,54 L0,40.5 L15.275475,0 L28.3506045,0 Z M22.1727116,23.4672897 L18.5759931,33.3084112 L25.8963731,33.3084112 L22.1727116,23.4672897 Z"
																id="Combined-Shape"
																fill="#FFFFFF" />
															<polygon id="Path-4" fill="#DE4141" points="0 40.5 11 40 8.5 54" />
														</g>
													</g>
												</g>
											</svg>
										</a>
									)}
								</div>
								{this.props.environment === "dev" && <div className="badge badge-info">Dev</div>}
								{this.props.environment === "staging" && <div className="badge badge-info">Staging</div>}
								{this.props.environment === "admin" && <div className="badge badge-info">Admin</div>}
								{this.props.environment === "superuser" && <div className="badge badge-info">Super User</div>}
								{this.props.environment === "helpdesk" && <div className="badge badge-info">Help Desk</div>}
								{this.props.environment === "online" && <div className="badge badge-success">Online</div>}
								{this.props.environment === "offline" && <div className="badge badge-danger">Online</div>}
							</div>
							{this.props.navigation && <div>{this.props.navigation}</div>}
						</div>

						<div className="appName" role="banner">
							{this.props.name}
              {this.props.subtitle ? <div className="appSubName">{this.props.subtitle}</div> : null}
						</div>
						<div className="profile-hover" role="navigation" aria-label="User.">
							{this.props.utilityNavigation && (
								<NavBar
									maxWidth={this.props.utilMaxWidth}
									minWidth={this.props.utilMinWidth}
									responsiveType={ResponsiveTypes.CollapseAll}
									type={NavBarTypes.IconOnly}
									tabs={this.props.utilityNavigation}
								/>
							)}
							<div>{this.props.profile}</div>
						</div>

						<section aria-live="polite" id="mainbar" className="mainbar" role="main" tabIndex={-1} ref={this.mainContentRef} aria-label={this.props.contentAriaLabel || "Main Page Area."}>
							{this.props.content}
						</section>
					</div>
				</Div100vh>
			</Fragment>
		);
	}
}
